import {
  getToggleButtonUtilityClass,
  toggleButtonClasses,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
} from '@mui/material'
import { useEffect, useLayoutEffect, useRef } from 'react'

// ----------------------------------------------------------------------
// A segmented control: two to four exclusive views of the same data.
//
// MUI's group draws the choice as a fill on the pressed cell, so a press
// swaps one fill for another in place and the control reads as a row of
// buttons in a box. This draws the fill once, as a thumb under the cells, and
// moves it to the cell pressed: one control with one thing in it that is set,
// travelling to wherever it is set to.
//
// The thumb is measured rather than computed. A segment is as wide as its
// label, a label can change while it is the one selected (Pick a month becomes
// the month picked), and the fonts arrive after the first layout, so the thumb
// takes the selected cell's own box and a ResizeObserver keeps it there. It
// reads `offsetLeft` and `offsetWidth` rather than a client rect, because a
// dialog's entrance is a transform and a rect read during it is the scaled one.
//
// How it looks and how it moves are the theme's (`overrides/ToggleButton`).
// This only says where the selected cell is and which colour it was asked for.
// ----------------------------------------------------------------------

/** The thumb's class, which the theme draws. */
export const SEGMENTED_THUMB_CLASS = 'beast-segmented-thumb'

/** The colours a cell can be asked for: the same list the theme styles. */
export const SEGMENT_COLORS = [
  'standard',
  'primary',
  'secondary',
  'error',
  'info',
  'success',
  'warning',
  'attention',
] as const

export type SegmentColor = (typeof SEGMENT_COLORS)[number]

/** The colour the cell was asked for, read off the class MUI gives it. */
const colorOf = (cell: HTMLElement): SegmentColor =>
  SEGMENT_COLORS.find((color) =>
    cell.classList.contains(getToggleButtonUtilityClass(color)),
  ) ?? 'standard'

/**
 * Puts the thumb under the selected cell.
 *
 * A thumb with nowhere to be (nothing selected, or a group not laid out yet
 * because it is in a closed panel) loses `data-placed`, which hides it, and
 * the next placement lands without travel. Otherwise it would slide in from
 * the group's corner on first paint, or from wherever it last was when a
 * hidden panel opens.
 */
const place = (group: HTMLElement, thumb: HTMLElement) => {
  const cell = group.querySelector<HTMLElement>(
    `:scope > .${toggleButtonClasses.selected}`,
  )
  if (!cell) {
    delete thumb.dataset.placed
    return
  }

  // The colour does not need a layout, so it is named even when there is none.
  thumb.dataset.color = colorOf(cell)

  if (cell.offsetWidth === 0) {
    delete thumb.dataset.placed
    return
  }

  const travels = thumb.dataset.placed !== undefined
  thumb.style.width = `${cell.offsetWidth}px`
  thumb.style.height = `${cell.offsetHeight}px`
  thumb.style.transform = `translate(${cell.offsetLeft}px, ${cell.offsetTop}px)`

  if (!travels) {
    // Land first, then switch travel on. Both in one frame would be one style
    // change, and the browser would animate the landing from the old box.
    thumb.getBoundingClientRect()
    thumb.dataset.placed = ''
  }
}

export interface SegmentedControlProps<T extends string>
  extends Omit<ToggleButtonGroupProps, 'value' | 'onChange' | 'exclusive'> {
  /** The segment that is the view. `null` selects none and draws no thumb. */
  value: T | null
  /**
   * The segment pressed. Pressing the one already selected changes nothing:
   * a segmented control always has a view, so it is never pressed off.
   */
  onChange?: (next: T) => void
}

export default function SegmentedControl<T extends string>({
  value,
  onChange,
  children,
  ...other
}: SegmentedControlProps<T>) {
  const groupRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLSpanElement>(null)
  const observer = useRef<ResizeObserver | null>(null)
  const observed = useRef(new WeakSet<Element>())

  // Every render, not only when `value` changes: a cell's label, and with it
  // its width, can change while the value stays the same.
  useLayoutEffect(() => {
    const group = groupRef.current
    const thumb = thumbRef.current
    if (!group || !thumb) return
    place(group, thumb)

    observer.current ??= new ResizeObserver(() => {
      if (groupRef.current && thumbRef.current) {
        place(groupRef.current, thumbRef.current)
      }
    })
    ;[group, ...Array.from(group.children)].forEach((element) => {
      if (element === thumb || observed.current.has(element)) return
      observer.current?.observe(element)
      observed.current.add(element)
    })
  })

  useEffect(
    () => () => {
      observer.current?.disconnect()
      observer.current = null
      observed.current = new WeakSet()
    },
    [],
  )

  return (
    <ToggleButtonGroup
      ref={groupRef}
      exclusive
      value={value}
      onChange={(_event, next: T | null) => {
        if (next !== null) onChange?.(next)
      }}
      {...other}
    >
      {/* Under the labels: the theme stacks it there, and it comes first. */}
      <span ref={thumbRef} className={SEGMENTED_THUMB_CLASS} aria-hidden />
      {children}
    </ToggleButtonGroup>
  )
}
